import "react-phone-input-2/lib/style.css";

import { useFormik } from "formik";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineChangeCircle, MdRemoveCircleOutline } from "react-icons/md";
import PhoneInput from "react-phone-input-2";

import { ReactComponent as EyeIcon } from "../../../assets/eye-closed.svg"; // Eye icon for showing password
import { ReactComponent as EyeSlashIcon } from "../../../assets/eye-open.svg";
import user_placeholder from "../../../assets/teacher_placeholder.svg";
import { useTheme } from "../../../context/ThemeContext";
import { useSnack } from "../../../hooks/useSnack";
import authServices from "../../../services/authServices";
import teacherServices from "../../../services/teacherServices";
import { getLanguage } from "../../../utils/getLanguage";
import { editTeacherModalValidationSchema, validatePhoneNumber } from "../../../utils/validationSchema";
import Button from "../../Button";
import LoadingBtn from "../../LoadingBtn";
import Modal from "../../Modal";

const EditTeacherModal = forwardRef(({ onSubmit }, ref) => {
  const { t } = useTranslation();
  const lang = getLanguage();
  const isRtl = lang === "ar";
  const { showSnack } = useSnack();
  const { theme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState("JO"); // Default country: Jordan

  useImperativeHandle(ref, () => ({
    openModal(teacher) {
      setSelectedImagePreview(teacher?.image || user_placeholder);
      formik.setValues({
        name: teacher?.name || "",
        username: teacher?.username || "",
        email: teacher?.email || "",
        phone: teacher?.phone_number || "",
        password: "",
        confirmPassword: "",
      });
      setTeacher(teacher);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const formik = useFormik({
    initialValues: {
      name: "",
      username: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: editTeacherModalValidationSchema(t, showChangePassword),
    validate: (values) => {
      const errors = {};
      const phoneError = validatePhoneNumber(values.phone, phoneCountryCode, t);
      if (phoneError) errors.phone = phoneError;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);

      try {
        const payload = new FormData();
        payload.append("name", values.name);
        payload.append("username", values.username);
        payload.append("email", values.email);
        payload.append("phone_number", values.phone);
        payload.append("_method", "PUT");
        values.password && payload.append("password", values.password);
        values.confirmPassword && payload.append("password_confirmation", values.confirmPassword);
        if (selectedImagePreview == user_placeholder) {
          await authServices.removeImage(teacher?.user_id);
          setSelectedImageFile(null);
        } else {
          selectedImageFile && payload.append("image", selectedImageFile);
        }
        const response = await teacherServices.editTeacherProfile(teacher.id, payload);

        showSnack(response.message, "success");
        onSubmit(values);
        resetForm();
        setIsOpen(false);
        setShowChangePassword(false);
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      } catch (error) {
        setIsSubmitting(false);
        setShowChangePassword(false);
        showSnack(error, "error");
      }
    },
  });

  // Toggle password visibility
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);
  const toggleChangePassword = () => setShowChangePassword(!showChangePassword);

  // Handle image selection and preview
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setSelectedImageFile(file); // Set the actual file, not the URL
      setSelectedImagePreview(imageUrl);
      event.target.value = ""; // Reset input value
    }
  };

  const handleCloseModal = () => {
    formik.resetForm(); // Reset the form
    setSelectedImagePreview(teacher.image ? teacher.image : user_placeholder); // Reset the image preview
    setIsOpen(false); // Close the modal
    setShowChangePassword(false);
  };

  const handleRemoveImage = () => {
    setSelectedImagePreview(user_placeholder);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      header={
        <div className="flex items-center justify-between">
          <p>{t("edit")}</p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button label={t("cancel")} onClick={handleCloseModal} color="white" variant="outlined" />
          <Button
            className="w-36"
            color="blue"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={formik.handleSubmit}
            label={t("done")}
          />
        </div>
      }
    >
      <div className="flex flex-col items-center sm:flex-row sm:items-start sm:gap-9">
        <div className="mb-4 flex flex-shrink-0 flex-col items-center sm:mb-0">
          <div className="relative">
            {selectedImagePreview ? (
              <img
                className="h-28 w-28 rounded-full border-4 border-blue-500 object-cover"
                src={selectedImagePreview}
                alt="Profile"
              />
            ) : (
              <img
                className="h-28 w-28 rounded-full border-4 border-blue-500 object-cover"
                src={user_placeholder}
                alt="Profile"
              />
            )}
            <div className="flex justify-center gap-1">
              <label htmlFor="imageUpload" className="group relative cursor-pointer rounded-md p-2">
                <div className="text-center text-sm text-blue-500">
                  <MdOutlineChangeCircle size={24} />
                </div>
                <span className="absolute left-1/2 top-[-30px] w-max -translate-x-1/2 scale-0 rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100">
                  {t("change")}
                </span>
                <input
                  id="imageUpload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>

              {selectedImagePreview !== user_placeholder ? (
                <label htmlFor="imageUpload" className="group relative cursor-pointer rounded-md p-2">
                  <span className="absolute left-1/2 top-[-30px] w-max -translate-x-1/2 scale-0 rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100">
                    {t("remove")}
                  </span>
                  <button onClick={handleRemoveImage}>
                    <MdRemoveCircleOutline className="text-blue-500" size={24} />
                  </button>
                </label>
              ) : null}
            </div>
          </div>
        </div>

        <div className="w-full space-y-1">
          {/* Form Fields */}
          {[
            {
              name: "name",
              label: t("name"),
              type: "text",
              placeholder: t("teacher_full_name"),
            },
            {
              name: "username",
              label: t("username"),
              type: "text",
              placeholder: t("username"),
            },
            {
              name: "email",
              label: t("email"),
              type: "email",
              placeholder: "example@example.com",
            },
          ].map((field, index) => (
            <div key={index}>
              <div key={field.name} className="flex items-center space-x-4">
                <div className={`flex items-end space-x-1 ${isRtl ? "w-52" : "w-36"}`}>
                  <label
                    className={`block text-sm font-medium text-gray-700 dark:text-dark-mutedText dark:text-mutedText`}
                  >
                    {field.label}
                  </label>
                  {formik.touched[field.name] &&
                    (formik.errors[field.name] === "Required" || formik.errors[field.name] === "مطلوب") && (
                      <span className="text-start text-xl leading-3 text-red-300">*</span>
                    )}
                </div>
                <input
                  type={field.type}
                  name={field.name}
                  placeholder={field.placeholder}
                  className="w-full border-b-2 border-gray-50 py-2 text-sm text-gray-400 placeholder:text-sm placeholder:text-mutedText focus:border-blue-500 focus:outline-none dark:border-[#FAFBFC0D] dark:bg-dark-surface dark:text-white"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values[field.name]}
                />
              </div>
              {formik.touched[field.name] &&
                formik.errors[field.name] !== "Required" &&
                formik.errors[field.name] !== "مطلوب" && (
                  <p className="mt-1 text-start text-xs text-red-300">{formik.errors[field.name]}</p>
                )}
            </div>
          ))}

          {/* Phone Field */}
          <>
            <div className="flex items-center space-x-4">
              <div className={`flex items-end space-x-1 ${isRtl ? "w-[230px]" : "w-[154px]"}`}>
                <label
                  className={`block text-sm font-medium text-gray-700 dark:text-dark-mutedText dark:text-mutedText`}
                >
                  {t("phone")}
                </label>
                {formik.touched.phone &&
                  (formik.errors.phone === "Required" || formik.errors.phone === "مطلوب") && (
                    <span className="text-start text-xl leading-3 text-red-300">*</span>
                  )}
              </div>
              <PhoneInput
                country="jo"
                placeholder={t("enter_phone_number")}
                excludeCountries={["il"]}
                value={formik.values.phone}
                onChange={(phone, { countryCode }) => {
                  formik.setFieldValue("phone", phone);
                  setPhoneCountryCode(countryCode.toUpperCase());
                }}
                inputStyle={{
                  width: "100%",
                  border: "none",
                  color: theme == "dark" ? "#ffff" : "#717275",
                  borderRadius: 0,
                  backgroundColor: theme === "dark" ? "transparent" : "",
                  borderBottomColor: theme === "dark" ? "#FAFBFC0D" : "#F1F5F9",
                  borderBottomWidth: "2px",
                  borderBottomStyle: "solid",
                }}
              />
            </div>
            {formik.touched.phone &&
              formik.errors.phone !== "Required" &&
              formik.errors.phone !== "مطلوب" && (
                <p className="mt-1 text-start text-xs text-red-300">{formik.errors.phone}</p>
              )}
          </>

          {/* Change Password Section */}
          <button
            onClick={toggleChangePassword}
            className="flex justify-self-end pt-1 text-xs font-semibold text-blue-500 transition duration-300 hover:text-blue-600"
          >
            {t("change_password")}
          </button>
          {showChangePassword && (
            <>
              {["password", "confirmPassword"].map((field, index) => (
                <div key={index}>
                  <div key={field} className="flex items-center space-x-2">
                    <div className={`flex items-end space-x-1 ${isRtl ? "w-[250px]" : "w-[240px]"}`}>
                      <label
                        className={`block text-sm font-medium text-gray-700 dark:text-dark-mutedText dark:text-mutedText`}
                      >
                        {t(field)}
                      </label>
                      {formik.touched[field] &&
                        (formik.errors[field] === "Required" || formik.errors[field] === "مطلوب") && (
                          <span className="text-start text-xl leading-3 text-red-300">*</span>
                        )}
                    </div>
                    <div className="relative w-full">
                      <input
                        type={
                          field === "password" && showPassword
                            ? "text"
                            : field === "confirmPassword" && showConfirmPassword
                              ? "text"
                              : "password"
                        }
                        name={field}
                        placeholder={t(field)}
                        className="w-full border-b-2 border-gray-50 py-2 text-sm text-gray-400 placeholder:text-sm placeholder:text-mutedText focus:border-blue-500 focus:outline-none dark:border-[#FAFBFC0D] dark:bg-dark-surface dark:text-white"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[field]}
                      />
                      <button
                        type="button"
                        onClick={
                          field === "password" ? togglePasswordVisibility : toggleConfirmPasswordVisibility
                        }
                        className={`absolute inset-y-0 flex items-center pr-3 text-sm font-semibold text-blue-500 ${
                          isRtl ? "left-0" : "right-0"
                        }`}
                      >
                        {field === "password" && showPassword ? (
                          <EyeSlashIcon className="h-4 w-4 text-mutedText dark:text-dark-mutedText" />
                        ) : field === "password" && !showPassword ? (
                          <EyeIcon className="h-4 w-4 text-mutedText dark:text-dark-mutedText" />
                        ) : field === "confirmPassword" && showConfirmPassword ? (
                          <EyeSlashIcon className="h-4 w-4 text-mutedText dark:text-dark-mutedText" />
                        ) : (
                          field === "confirmPassword" &&
                          !showConfirmPassword && (
                            <EyeIcon className="h-4 w-4 text-mutedText dark:text-dark-mutedText" />
                          )
                        )}
                      </button>
                    </div>
                  </div>
                  {formik.touched[field] &&
                    formik.errors[field] !== "Required" &&
                    formik.errors[field] !== "مطلوب" && (
                      <p className="mt-1 text-start text-xs text-red-300">{formik.errors[field]}</p>
                    )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
});

export default EditTeacherModal;
